/**
 * Soiam Theme Animations
 * Includes GSAP animations, scroll triggers, and Lenis smooth scrolling
 * Based on the original Webflow Hollow template animations
 */

// Initialize Lenis immediately (before DOMContentLoaded) to prevent scroll "shock"
// Lenis only needs the <html> element which is available immediately
if (typeof Lenis !== 'undefined') {
  initSmoothScrolling();
}

// Wait for DOM to be ready for other animations
document.addEventListener('DOMContentLoaded', function() {
  // Initialize GSAP animations
  if (typeof gsap !== 'undefined') {
    initGSAPAnimations();
  }

  // Initialize video interactions
  initVideoInteractions();
});

/**
 * Initialize Lenis smooth scrolling
 * Replicates the smooth scroll behavior from the original template
 */
function initSmoothScrolling() {
  const lenis = new Lenis({
    lerp: 0.1,
    wheelMultiplier: 0.7,
    gestureOrientation: 'vertical',
    normalizeWheel: false,
    smoothTouch: false,
  });

  function raf(time) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }
  requestAnimationFrame(raf);

  // Update ScrollTrigger on Lenis scroll
  if (typeof ScrollTrigger !== 'undefined') {
    lenis.on('scroll', ScrollTrigger.update);
  }
}

/**
 * Initialize GSAP animations and scroll triggers
 */
function initGSAPAnimations() {
  // Register GSAP plugins
  if (typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
  }
  if (typeof Observer !== 'undefined') {
    gsap.registerPlugin(Observer);
  }
  if (typeof SplitText !== 'undefined') {
    gsap.registerPlugin(SplitText);
  }

  // Global animation settings
  gsap.defaults({
    ease: 'power2.out',
    duration: 1
  });

  // Initialize different animation types
  initFadeAnimations();
  initBlurAnimations();
  initTextAnimations();
  initImageEffects();
  initScrollReveal();
}

/**
 * Fade animations for elements
 */
function initFadeAnimations() {
  // Fade up animations
  const fadeUpElements = document.querySelectorAll('[data-animate="fade-up"]');
  if (fadeUpElements.length > 0) {
    gsap.set(fadeUpElements, { opacity: 0, y: 50 });

    ScrollTrigger.batch(fadeUpElements, {
      onEnter: (elements) => {
        gsap.to(elements, {
          opacity: 1,
          y: 0,
          duration: 1.2,
          stagger: 0.15,
          ease: 'power2.out'
        });
      },
      start: 'top 85%',
      once: true
    });
  }

  // Fade left animations
  const fadeLeftElements = document.querySelectorAll('[data-animate="fade-left"]');
  if (fadeLeftElements.length > 0) {
    gsap.set(fadeLeftElements, { opacity: 0, x: 50 });

    ScrollTrigger.batch(fadeLeftElements, {
      onEnter: (elements) => {
        gsap.to(elements, {
          opacity: 1,
          x: 0,
          duration: 1.2,
          ease: 'power2.out'
        });
      },
      start: 'top 80%',
      once: true
    });
  }

  // Fade right animations
  const fadeRightElements = document.querySelectorAll('[data-animate="fade-right"]');
  if (fadeRightElements.length > 0) {
    gsap.set(fadeRightElements, { opacity: 0, x: -50 });

    ScrollTrigger.batch(fadeRightElements, {
      onEnter: (elements) => {
        gsap.to(elements, {
          opacity: 1,
          x: 0,
          duration: 1.2,
          ease: 'power2.out'
        });
      },
      start: 'top 80%',
      once: true
    });
  }
}

/**
 * Blur animations (matching Webflow style)
 */
function initBlurAnimations() {
  const blurElements = document.querySelectorAll('[data-animate="fade-blur"]');
  if (blurElements.length > 0) {
    gsap.set(blurElements, {
      filter: 'blur(12px)',
      opacity: 0
    });

    // Animate on page load for hero elements
    if (blurElements[0].closest('.soiam-hero')) {
      gsap.to(blurElements, {
        filter: 'blur(0px)',
        opacity: 1,
        duration: 1.5,
        stagger: 0.3,
        ease: 'power2.out',
        delay: 0.5
      });
    } else {
      // Animate on scroll for other elements
      ScrollTrigger.batch(blurElements, {
        onEnter: (elements) => {
          gsap.to(elements, {
            filter: 'blur(0px)',
            opacity: 1,
            duration: 1.5,
            stagger: 0.2,
            ease: 'power2.out'
          });
        },
        start: 'top 80%',
        once: true
      });
    }
  }

  // Blur with movement
  const blurUpElements = document.querySelectorAll('[data-animate="fade-blur-up"]');
  if (blurUpElements.length > 0) {
    gsap.set(blurUpElements, {
      filter: 'blur(12px)',
      opacity: 0,
      y: 50
    });

    ScrollTrigger.batch(blurUpElements, {
      onEnter: (elements) => {
        gsap.to(elements, {
          filter: 'blur(0px)',
          opacity: 1,
          y: 0,
          duration: 1.2,
          stagger: 0.2,
          ease: 'power2.out'
        });
      },
      start: 'top 80%',
      once: true
    });
  }
}

/**
 * Text animations using SplitText
 */
function initTextAnimations() {
  if (typeof SplitText === 'undefined') return;

  const animatedTexts = document.querySelectorAll('[data-animate="split-text"]');
  animatedTexts.forEach(text => {
    const split = new SplitText(text, { type: 'words,chars' });

    gsap.set(split.chars, { opacity: 0, y: 20 });

    ScrollTrigger.create({
      trigger: text,
      start: 'top 80%',
      onEnter: () => {
        gsap.to(split.chars, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.02,
          ease: 'power2.out'
        });
      },
      once: true
    });
  });
}

/**
 * Image effects and hover animations
 */
function initImageEffects() {
  // Polaroid floating animation
  const polaroids = document.querySelectorAll('.soiam-studio__polaroid');
  polaroids.forEach((polaroid, index) => {
    gsap.to(polaroid, {
      y: '+=15',
      duration: 3 + index * 0.5,
      ease: 'power1.inOut',
      yoyo: true,
      repeat: -1,
      delay: index * 0.3
    });
  });

  // Image hover effects
  const imageEffects = document.querySelectorAll('[image-effect]');
  imageEffects.forEach(element => {
    const image = element.querySelector('img');
    if (image) {
      element.addEventListener('mouseenter', () => {
        gsap.to(image, {
          scale: 1.05,
          duration: 0.4,
          ease: 'power2.out'
        });
      });

      element.addEventListener('mouseleave', () => {
        gsap.to(image, {
          scale: 1,
          duration: 0.4,
          ease: 'power2.out'
        });
      });
    }
  });
}

/**
 * Scroll reveal animations for various elements
 */
function initScrollReveal() {
  // Navigation animation on scroll
  const navbar = document.querySelector('.navbar');
  if (navbar) {
    ScrollTrigger.create({
      start: 'top -100',
      end: 99999,
      toggleClass: {className: 'scrolled', targets: navbar}
    });
  }

  // Parallax effects for background elements
  const parallaxElements = document.querySelectorAll('[data-parallax]');
  parallaxElements.forEach(element => {
    const speed = element.dataset.parallax || 0.5;

    gsap.to(element, {
      yPercent: -50 * speed,
      ease: 'none',
      scrollTrigger: {
        trigger: element,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  });

  // Stagger animations for grids
  const gridElements = document.querySelectorAll('.soiam-classes__cards, .soiam-blog__articles');
  gridElements.forEach(grid => {
    const items = grid.children;

    gsap.set(items, { opacity: 0, y: 30 });

    ScrollTrigger.create({
      trigger: grid,
      start: 'top 80%',
      onEnter: () => {
        gsap.to(items, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: 'power2.out'
        });
      },
      once: true
    });
  });
}

/**
 * Video interactions
 */
function initVideoInteractions() {
  // Video play on hover for expandable cards
  const videoCards = document.querySelectorAll('.soiam-classes__card');
  videoCards.forEach(card => {
    const video = card.querySelector('video');
    if (video) {
      // Ensure video starts paused
      video.pause();
      video.currentTime = 0;

      card.addEventListener('mouseenter', () => {
        video.play().catch(e => console.log('Video play failed:', e));
      });

      card.addEventListener('mouseleave', () => {
        video.pause();
        video.currentTime = 0;
      });
    }
  });

  // Background video autoplay with intersection observer
  const backgroundVideos = document.querySelectorAll('.soiam-hero__video, .soiam-about__video');

  if ('IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        if (entry.isIntersecting) {
          video.play().catch(e => console.log('Background video play failed:', e));
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.5 });

    backgroundVideos.forEach(video => {
      videoObserver.observe(video);
    });
  }
}

/**
 * Utility function to create floating animations
 */
function createFloatingAnimation(elements, options = {}) {
  const {
    yRange = 20,
    duration = 3,
    stagger = 0.2,
    ease = 'power1.inOut'
  } = options;

  elements.forEach((element, index) => {
    gsap.to(element, {
      y: `+=${yRange}`,
      duration: duration,
      ease: ease,
      yoyo: true,
      repeat: -1,
      delay: index * stagger
    });
  });
}

/**
 * Refresh ScrollTrigger on window resize
 */
window.addEventListener('resize', () => {
  if (typeof ScrollTrigger !== 'undefined') {
    ScrollTrigger.refresh();
  }
});

// Export functions for use in individual sections
window.SoiamAnimations = {
  createFloatingAnimation,
  initVideoInteractions,
  initGSAPAnimations
};